import React, { useCallback, useMemo, useState } from 'react';

import { Button } from '@eika/button';
import { useViewType, ViewType } from '@eika/hooks';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { EikaModal } from '../../components/EikaModal';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useAccounts } from '../../fetchHooks/useAccount';
import { useAvtalegiro, useAvtalegiroDetails } from '../../fetchHooks/useAvtalegiro';
import type { PaymentAgreementId } from '../../generated-types';
import { useReceiver } from '../../hooks/useSetReceiver';
import useTranslation from '../../hooks/useTranslation';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import { paymentTrackIDs } from '../../utils/gtm/trackIDs/payment';
import AvtaleGiroDetailsForm from './AvtaleGiroDetailsForm';
import AvtaleGiroDetailsSkeleton from './AvtaleGiroDetailsSkeleton';

interface Props {
    id: PaymentAgreementId;
    show: boolean;
    setShow: React.Dispatch<boolean>;
    refreshAvtaleGiroList: () => void;
    onClose: () => void;
}

const AvtaleGiroDetails: React.FC<Props> = ({ id, show, setShow, onClose, refreshAvtaleGiroList }) => {
    const translated = useTranslation();
    const [isDeleted, setDeleted] = useState(false);

    const { dispatch, signingStatus } = useSigningContext();
    const { isLoading: accountsLoading, error: accountsError, refetch: refreshAccounts } = useAccounts();
    const {
        data: avtalegiro,
        error: avtalegiroDetailsError,
        isLoading: isLoadingAvtaleGiroAgreement,
        invalidateQuery,
        refetch: refreshDetails,
        isStale,
    } = useAvtalegiroDetails(id);

    const { data: avtalegiros } = useAvtalegiro();
    const isBlocked = useMemo(() => avtalegiros?.find((it) => it.id === id)?.status === 'UNKNOWN', [avtalegiros, id]);

    const receiver = useReceiver(avtalegiro?.toAccount);
    const isLoading = isLoadingAvtaleGiroAgreement || !avtalegiro || accountsLoading || !receiver || isStale;
    const error = avtalegiroDetailsError ?? accountsError;

    const onCloseUpdate = useCallback(() => {
        dispatch({ type: Actions.RESET });
        if (signingStatus === 'UNKNOWN' || signingStatus === 'REJECTED' || signingStatus === 'COMPLETED') {
            invalidateQuery();
            refreshAvtaleGiroList();
        } else if (isDeleted) {
            refreshAvtaleGiroList();
        }
        onClose();
    }, [dispatch, signingStatus, isDeleted, onClose, invalidateQuery, refreshAvtaleGiroList]);

    const refresh = async () => {
        await refreshDetails();
        await refreshAccounts();
    };

    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;

    return (
        <EikaModal
            id="avtaleGiroDetails"
            className="avtalegiro-details-modal"
            open={show}
            setOpen={setShow}
            onClose={onCloseUpdate}
            timeOut={signingStatus === 'COMPLETED' || isDeleted ? 2500 : undefined}
            contentShouldStretch
            size="wide"
            desktopMinHeight="small"
            appNamespace="dagligbank-lokalbank-web"
            dataTrackName={paymentTrackIDs.avtalegiro.avtalegiroName}
        >
            {error && !isLoading && (
                <ErrorCardWrapper className="avtalegiro__error-view" exception={error}>
                    <Button onClick={refresh}>{translated.tryAgain}</Button>
                </ErrorCardWrapper>
            )}
            {isLoading && <AvtaleGiroDetailsSkeleton isMobile={isMobile} numberOfSkeletonItems={4} />}
            {!isLoading && avtalegiro && (
                <AvtaleGiroDetailsForm
                    id={id}
                    toAccount={receiver}
                    avtalegiro={avtalegiro}
                    onClose={onClose}
                    isBlocked={isBlocked}
                    onDeleted={() => setDeleted(true)}
                />
            )}
        </EikaModal>
    );
};

export default (props: Props) => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.UPDATE_AVTALEGIRO}>
        <AvtaleGiroDetails {...props} />
    </SigningContextProvider>
);
